/**
 * Batch H1 — Claim lifecycle display: link used → entitlement saved → NFT issuance state.
 * Issuance is shown as issued ONLY when the backend verified on-chain ownership.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import theme from '../theme';

const ISSUANCE_COLOR = { issued: '#065F46', failed: '#991B1B', not_available: '#92400E' };

function Step({ done, icon, label, detail, testid, color }) {
  const Icon = icon || (done ? CheckCircle2 : Clock);
  return (
    <div data-testid={testid} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <Icon size={16} color={color || (done ? theme.colors.status.success : theme.colors.text.muted)} style={{ marginTop: 2, flexShrink: 0 }} />
      <div>
        <div style={{ fontWeight: 700, fontSize: 13, color: theme.colors.text.primary }}>{label}</div>
        {detail && <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

export function ClaimLifecycle({ claimedAt, entitlementStatus, issuance, testidPrefix = 'lifecycle' }) {
  const st = issuance?.status || 'not_available';
  const issued = st === 'issued';
  const issuanceDetail = issued
    ? `Token #${issuance.token_id ?? '—'}${issuance.contract_address ? ` · ${issuance.contract_address.slice(0, 10)}…` : ''}`
    : issuance?.reason;
  return (
    <div data-testid={`${testidPrefix}-steps`} style={{ display: 'grid', gap: 10 }}>
      <Step done testid={`${testidPrefix}-link-used`} label="Claim link used"
        detail={claimedAt ? `Claimed ${new Date(claimedAt).toLocaleString()}` : null} />
      <Step done={entitlementStatus === 'active'} testid={`${testidPrefix}-entitlement`}
        label={entitlementStatus === 'active' ? 'Entitlement saved' : `Entitlement ${entitlementStatus || 'pending'}`}
        detail="Your access to this Kickback is recorded on your account." />
      <Step done={issued} icon={st === 'failed' ? XCircle : undefined} color={ISSUANCE_COLOR[st]}
        testid={`${testidPrefix}-issuance`}
        label={`NFT issuance: ${issuance?.label || 'Not available yet'}`} detail={issuanceDetail} />
    </div>
  );
}

const btn = (primary) => ({
  padding: '10px 16px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13,
  background: primary ? theme.colors.primary : 'transparent', color: primary ? '#fff' : theme.colors.primary,
  border: `1px solid ${theme.colors.primary}`,
});

const DASHBOARDS = { creator: '/creator/dashboard', broker: '/broker/dashboard', admin: '/admin/dashboard', architect: '/architect/dashboard' };

function useRole(isLoggedIn) {
  const [role, setRole] = useState(null);
  useEffect(() => {
    if (!isLoggedIn) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => setRole(r.data?.role || 'unknown')).catch(() => setRole('unknown'));
  }, [isLoggedIn]);
  return role;
}

export function ClaimNavButtons({ isLoggedIn, next }) {
  const navigate = useNavigate();
  const role = useRole(isLoggedIn);
  if (isLoggedIn && role && role !== 'buyer') {
    return (
      <div data-testid="claim-nav-buttons" style={{ marginTop: 14 }}>
        <div data-testid="claim-nav-non-holder-note" style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 8 }}>
          You're signed in with a {role === 'unknown' ? 'non-Holder' : role} account. Claims belong to Holder accounts, so there are no claims to show here.
        </div>
        <button data-testid="claim-nav-role-dashboard-btn" style={btn(true)} onClick={() => navigate(DASHBOARDS[role] || '/')}>Go to my dashboard</button>
      </div>
    );
  }
  return (
    <div data-testid="claim-nav-buttons" style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {isLoggedIn ? (
        <>
          <button data-testid="claim-nav-my-claims-btn" style={btn(true)} onClick={() => navigate('/buyer/claims')}>View My Claims</button>
          <button data-testid="claim-nav-dashboard-btn" style={btn(false)} onClick={() => navigate('/buyer/dashboard')}>Back to Dashboard</button>
        </>
      ) : (
        <>
          <button data-testid="claim-nav-signin-btn" style={btn(true)} onClick={() => navigate(`/auth${next ? `?next=${next}` : ''}`)}>Sign in</button>
          <button data-testid="claim-nav-home-btn" style={btn(false)} onClick={() => navigate('/')}>Go to Home</button>
        </>
      )}
    </div>
  );
}
